import { Subscription } from 'rxjs';
import { NameValue } from '../global/types';

export const quarterMap = ['', '一', '二', '三', '四'];

export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
export const HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

export const SCALE_POS = [1, 10, 100, 1_000, 10_000, 100_000, 1_000_000, 10_000_000, 100_000_000, 1_000_000_000];
export const SCALE_NEG = [0.1, 0.01, 0.001, 0.0001, 0.00001, 0.000001, 0.0000001, 0.00000001, 0.000000001, 0.0000000001];

export class AbmUtil {

    public static findInArray<T>(array: T[], field: number | string, key: number | string, contain = false): T {

        const index = this.indexInArray(array, field, key, contain);

        return index >= 0 ? array[index] : undefined;
    }

    public static indexInArray<T>(array: T[], field: string | number, key: number | string, contain = false): number {
        if (array?.length === 0) {
            return -1;
        }

        if (typeof field === 'string') {
            this.checkProperties(array[0], field);
        }

        for (let i = 0; i < array.length; i++) {
            const element = array[i];
            if (element[field] === key) {
                return i;
            }

            if (contain && element[field].toString().includes(key)) {
                return i;
            }
        }
        return -1;
    }

    public static sortArray<T>(array: T[], sortBy?: string | string[], desc = false) {
        if (!array || array.length === 0) {
            return array;
        }

        return array.sort((a, b) => {
            if (a === null || a === undefined) {
                return b === null || b === undefined ? 0 : (desc ? -1 : 1);
            }

            if (!sortBy &&
                (typeof a === 'string' || typeof a === 'number') &&
                (typeof b === 'string' || typeof b === 'number')) {
                return this.compare(a as number | string, b);
            }

            let result = 0;
            let fields: string[];
            if (typeof sortBy === 'string') {
                fields = [sortBy];
            } else {
                fields = sortBy;
            }
            for (const f of fields) {
                this.checkProperties(a, f);
                this.checkProperties(b, f);
                result = this.compare(a[f], b[f]);
                if (result !== 0) {
                    return desc ? -result : result;
                }
            }
            return result;
        });
    }

    public static createArray<T>(length = -1, fill: T = null): T[] {
        let ret: T[];
        if (length == -1) {
            ret = new Array();
        } else {
            ret = new Array(length);
        }
        ret.fill(fill);
        return ret;
    }

    public static isGT(i: string, j: string): boolean {
        return this.compare(i, j) > 0;
    }

    /**
     * compare 2 number|string
     *
     * return:
     * -1: op1 < op2
     *  1: op1 > op2
     *
     * @param op1 op1
     * @param op2 op2
     */
    public static compare(op1: number | string, op2: number | string): number {

        if (typeof op1 === 'number' && typeof op2 === 'number') {
            return op1 - op2;
        }

        const str1 = op1.toString();
        const str2 = op2.toString();
        if (str1.length !== str2.length) {
            return str1.length < str2.length ? -1 : 1;
        }
        return str1.localeCompare(str2);
    }

    public static getPropertyOrOthers<T>(obj: any, property: string, other?: any): T {
        if (obj.hasOwnProperty(property)) {
            return obj[property] as T;
        } else if (other) {
            return other as T;
        } else {
            return null;
        }
    }

    public static removeFromArray<T>(array: T[], field: string, key: string | number, contain = false): T[] {
        const ret = array.filter(a => {
            if (a.hasOwnProperty(field)) {
                return contain ? !a[field].toString().includes(key) : a[field] !== key;
            } else {
                return true;
            }
        });
        return ret;
    }

    public static replaceInArray<T>(array: T[], newItem: T, field: string, key: string | number): T[] {
        const i = this.indexInArray(array, field, key);
        if (i >= 0) {
            return array.splice(i, 1, newItem);
        } else {
            return undefined;
        }

    }

    public static checkProperties(data: any, ...properties: string[]) {
        for (const property of properties) {
            if (property && !data.hasOwnProperty(property)) {
                throw new Error(property + ' is not property of data');
            }
        }
    }

    public static getFileNameFromUrl(url: string): string {
        return url.split('/').pop().split('#')[0].split('?')[0];
    }

    public static extractPartOfTime(src: string, startPos: number, length?: number, append?: string) {
        return src.substr(startPos, length ?? 2) + (append ?? '');
    }

    public static objectToParamStr(body: { [name: string]: any }): string {
        let ret = '?';
        for (const k in body) {
            if (body.hasOwnProperty(k)) {
                const value = body[k];
                if (ret === '?') {
                    ret = ret + k + '=' + value.toString();
                } else {
                    ret = ret + '&' + k + '=' + value.toString();
                }
            }
        }
        return ret;
    }

    public static hourStr(i: number): string {
        const j = Math.round(i);
        return (j + 100).toString().substr(1) + ':00';
    }

    public static quarterStr(i: number): string {
        this.assert(i >= 1 && i <= 4, 'Quarter index out of bounds:' + i);

        return quarterMap[i] + '季度';
    }

    public static dashStr(...s: string[]): string { return s.join('-'); }

    public static sleep(ms) {
        const ret = new Promise(resolve => setTimeout(resolve, ms));
        return ret;
    }

    public static dateAdd(offset: number, target?: Date): Date {

        const from = target ? new Date(target) : new Date();

        if (offset === 0) {
            return from;
        }

        return new Date(from.setDate(from.getDate() + offset));
    }

    /**
     * Transfer {key1:[v1,v2],key2:[v3,v4]} -> [{key1:v1,key2:v3},{key1:v2,key2:v4}]
     */
    public static zipData(obj: { [key: string]: any[] }): { [key: string]: any }[] {
        const ret: { [key: string]: any }[] = [];
        for (const k in obj) {
            if (obj.hasOwnProperty(k)) {
                const arr = obj[k];
                for (let i = 0; i < arr.length; i++) {
                    const each = arr[i];
                    if (ret[i]) {
                        ret[i][k] = each;
                    } else {
                        const yo: { [key: string]: any } = {};
                        yo[k] = each;
                        ret.push(yo);
                    }
                }
            }
        }
        return ret;
    }

    /**
     * Transfer [{key1:v1,key2:v3},{key1:v2,key2:v4}] -> {key1:[v1,v2],key2:[v3,v4]}
     */
    public static unzipData(obj: { [key: string]: any }[]): { [key: string]: any[] } {
        const ret: any = {};

        for (const o of obj) {
            for (const k in o) {
                if (o.hasOwnProperty(k)) {
                    if (ret.hasOwnProperty(k)) {
                        ret[k].push(o[k]);
                    } else {
                        ret[k] = [o[k]];
                    }
                }
            }
        }
        return ret;
    }

    /**
     * get keys and values from an object.
     *
     * @param obj the object to be transferred
     */
    public static keysAndValues(obj: { [key: string]: any }): { keys: string[], values: any[] } {
        const keys = new Array<string>();
        const values = new Array<any>();
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                keys.push(key);
                values.push(obj[key]);
            }
        }
        return { keys, values };
    }

    public static maxMinAvg(data: number[], decimal = 0): { max: number, min: number, avg: number } {
        if (data.length === 0) {
            return { max: 0, min: 0, avg: 0 };
        }

        const max = Math.max(...data) || 0;
        const min = Math.min(...data) || 0;
        const avg = (data.reduce((a, b) => a + b, 0)) / data.length || 0;
        return { max, min, avg: +(avg.toFixed(decimal)) };
    }

    /**
     * return array of 3 values: max, min, avg
     * @param data input data array
     * @param decimal result round decimal
     */
    public static maxMinAvgArray(data: number[], decimal = 0): Array<number> {
        const result = this.maxMinAvg(data, decimal);
        return [result.max, result.min, result.avg];
    }

    public static hexToRgb(hex: string, alpha?: number, returnString = true)
        : string | { r: number, g: number, b: number } {
        const rgb: number[] = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
            , (m, r, g, b) => '#' + r + r + g + g + b + b)
            .substring(1).match(/.{2}/g)
            .map(x => parseInt(x, 16));

        const rgbObj = { r: rgb[0], g: rgb[1], b: rgb[2] };
        if (returnString) {
            return alpha >= 0 ? ('rgba(' + rgbObj.r + ',' + rgbObj.g + ',' + rgbObj.b + ',' + alpha + ')') :
                ('rgb(' + rgbObj.r + ',' + rgbObj.g + ',' + rgbObj.b + ')');
        } else {
            return rgbObj;
        }
    }

    public static echartSize(size = 16): number {
        const clientWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        return clientWidth ? size * (clientWidth / 1920) : size;
    }

    public static formPath(file: string, ...paths: string[]): string {
        let url = '';
        for (const p of paths) {
            url += (p.endsWith('/') ? p : (p + '/'));
        }

        return url + file;
    }

    public static assert(condition: any, msg?: string): asserts condition {
        if (!condition) {
            throw new Error(msg);
        }
    }

    public static transferUnit(num: number, basicTitle = '', askScale = false): NameValue {
        let ret: NameValue;
        let scale = 1;
        if (num < 1000) {
            ret = new NameValue('', num);
        } else if (num < 1000_000) {
            scale = 1_000;
            ret = new NameValue('k', num / scale);
        } else if (num < 1000_000_000) {
            scale = 1_000_000;
            ret = new NameValue('m', num / scale);
        } else if (num < 1000_000_000_000) {
            scale = 1_000_000_000;
            ret = new NameValue('mk', num / scale);
        } else if (num < 1000_000_000_000_000) {
            scale = 1_000_000_000_000;
            ret = new NameValue('mm', num / scale);
        } else {
            ret = new NameValue('', num);
        }

        ret.name = (ret.name + basicTitle).replace('kk', 'm');

        if (askScale) {
            (ret as any).scale = scale;
        }

        return ret;
    }

    public static transferCnUnit(num: number, basicTitle = '', askScale = false): NameValue {
        let ret: NameValue;
        let scale = 1;
        if (num < 1_0000) {
            ret = new NameValue('', num);
        } else if (num < 10_0000) {
            scale = 1_0000;
            ret = new NameValue('万', num / scale);
        } else if (num < 1000_0000) {
            scale = 100_0000;
            ret = new NameValue('百万', num / scale);
        } else if (num < 1_0000_0000) {
            scale = 1000_0000;
            ret = new NameValue('千万', num / scale);
        } else if (num >= 1_0000_0000) {
            scale = 1_0000_0000;
            ret = new NameValue('亿', num / scale);
        }

        ret.name = (ret.name + basicTitle);

        if (askScale) {
            (ret as any).scale = scale;
        }

        return ret;
    }

    private static _getScale(num: number, basicTitle = '', language: 'en' | 'cn' = 'en'): { name: string, value: number | string | boolean, scale?: number } {
        let res: { name: string, value: number | string | boolean, scale?: number };

        if (language === 'en') {
            res = AbmUtil.transferUnit(num, basicTitle, true);
        } else {
            res = AbmUtil.transferCnUnit(num, basicTitle, true);
        }
        return res;
    }

    public static transferUnitForMultiArray(nums: number[][], basicTitle = '', language: 'en' | 'cn' = 'en'): { name: string, nums: number[][] } {
        const omitZeros = nums.map(ns => ns.filter(n => n > 0));
        const min = Math.min(...omitZeros.map(ns => Math.min(...ns)));

        const res = AbmUtil._getScale(min, basicTitle, language);

        return { name: res.name, nums: nums.map(ns => ns.map(n => Math.round(n / res.scale))) };
    }

    public static transferUnitForArray(nums: number[], basicTitle = '', language: 'en' | 'cn' = 'en'): { name: string, nums: number[] } {
        const omitZeros = nums.filter(n => n > 0);
        const min = Math.min(...omitZeros);

        const res = AbmUtil._getScale(min, basicTitle, language);

        return { name: res.name, nums: nums.map(n => Math.round(n / res.scale)) };
    }

    /**
     *
     * @param type period type, currently support month, day, hour
     * @param all whether get periods untill today or get all periods.
     */
    public static getPeriods(type: 'M' | 'D' | 'H', all = false): number[] {
        let base: number[];
        let sliceTo: number;
        switch (type) {
            case 'M':
                base = MONTHS;
                if (all) { return base; }
                sliceTo = (new Date()).getMonth() + 1;
                break;
            case 'D':
                base = DAYS;
                if (all) { return base; }
                sliceTo = (new Date()).getDate();
                break;
            case 'H':
                base = HOURS;
                if (all) { return base; }
                sliceTo = (new Date()).getHours() + 1;
                break;
        }

        if (all) {
            return base;
        } else {
            return base.slice(0, sliceTo);
        }
    }

    public static get10Scale(scale: number) {
        if (scale > -1 && scale < SCALE_POS.length) {
            return SCALE_POS[scale];
        } else if (scale < 0 && (-scale) <= SCALE_NEG.length) {
            return SCALE_NEG[-scale - 1];
        } else {
            return Math.pow(10, scale);
        }
    }

    public static randomInRange(from = 0, to = 100, int = true): number {
        AbmUtil.assert(from < to, 'from must be less than to');

        const result = Math.random() * (to - from) + from;

        return int ? Math.round(result) : result;
    }

    public static alignWithZeroPrefix(nums: number[]): string[] {

        const maxLength = Math.max(...nums).toString().length;

        const scale = AbmUtil.get10Scale(maxLength);

        return nums.map(n => (n + scale).toString().substr(1));
    }

    public static unsubscribe(...subs: Subscription[]) {
        for (let sub of subs) {
            if (sub) {
                sub.unsubscribe();
                sub = null;
            }
        }
    }

}
